import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    ArrowDownLeft, ArrowUpRight, Banknote, ChevronLeft, ChevronRight, Landmark, Coffee, HandCoins, LogOut,
    Lock, Unlock, Plus, X, Loader2, AlertCircle, CheckCircle2, FileBarChart, Ban, MoreHorizontal, CalendarRange, CalendarDays, AlertTriangle,
} from 'lucide-react';
import GlobalDateFilter from '../../components/GlobalDateFilter/GlobalDateFilter';
import styles from './CashFlow.module.css';
import { useStore } from '../../context/StoreContext';
import { useAuth } from '../../context/AuthContext';
import { SMIIFY_API } from '../../config/api';
import { inr, shiftYmd, todayYmd, timeOf, longDate } from '../../utils/money';

const ACTIONS = [
    { category: 'OWNER_CASH_IN', label: 'Owner gave cash', hint: 'Change or float given by the owner', icon: HandCoins, dir: 'IN' },
    { category: 'BANK_DEPOSIT', label: 'Bank deposit', hint: 'Cash taken to the bank', icon: Landmark, dir: 'OUT', ref: 'Deposit slip no. (optional)' },
    { category: 'PETTY_EXPENSE', label: 'Petty expense', hint: 'Tea, cleaning, small purchases', icon: Coffee, dir: 'OUT', noteRequired: true },
    { category: 'OWNER_WITHDRAWAL', label: 'Owner took cash', hint: 'Cash taken out by the owner', icon: LogOut, dir: 'OUT' },
];
const MORE = [
    { category: 'OPENING_FLOAT', label: 'Opening cash', hint: 'Cash already in the drawer when you start using Hirsta', icon: Banknote, dir: 'IN' },
    { category: 'OTHER_IN', label: 'Other cash in', hint: 'Any other cash put in the drawer', icon: ArrowDownLeft, dir: 'IN', noteRequired: true },
    { category: 'OTHER_OUT', label: 'Other cash out', hint: 'Any other cash taken from the drawer', icon: ArrowUpRight, dir: 'OUT', noteRequired: true },
];
const NOTES = ['500', '200', '100', '50', '20', '10', '2000'];

export default function CashFlow() {
    const navigate = useNavigate();
    const { activeStore } = useStore();
    const { hasPermission } = useAuth();
    const canSeeReport = hasPermission('reports', 'view_daily_report') || hasPermission('reports', 'view_eod_report');
    const storeId = activeStore?._id || activeStore?.id;
    const isHQ = !storeId || storeId === 'global_hq' || activeStore?.isHQ;
    const [params, setParams] = useSearchParams();
    const isYmd = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v || '');
    const date = isYmd(params.get('date')) ? params.get('date') : todayYmd();
    const setDate = (d) => setParams(d === todayYmd() ? {} : { date: d });
    // date range view: ?view=range&from=&to=
    const rangeView = params.get('view') === 'range';
    const range = { startDate: isYmd(params.get('from')) ? params.get('from') : shiftYmd(todayYmd(), -6), endDate: isYmd(params.get('to')) ? params.get('to') : todayYmd() };
    const setRange = (r) => setParams({ view: 'range', from: r.startDate, to: r.endDate });

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [toast, setToast] = useState(null);
    const [form, setForm] = useState(null);   // the action being entered
    const [showMore, setShowMore] = useState(false);
    const [closeOpen, setCloseOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const headers = useMemo(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('smiify_token')}`, 'x-store-id': storeId }), [storeId]);
    const say = (type, text) => { setToast({ type, text }); setTimeout(() => setToast(null), 3500); };
    const api = async (path, opts = {}) => {
        const res = await fetch(`${SMIIFY_API}/cash-flow${path}`, { headers, ...opts });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json.success === false) throw new Error(json.message || `Request failed (${res.status}).`);
        return json;
    };

    const load = useCallback(async () => {
        if (isHQ || rangeView) { setLoading(false); return; }
        setLoading(true); setError('');
        try { setData((await api(`?date=${date}`)).data); } catch (e) { setError(e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message); } finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, storeId, isHQ, rangeView]);
    useEffect(() => { load(); }, [load]);

    const d = data?.drawer;
    const perms = data?.permissions || {};
    const blind = Boolean(data?.blind);
    const closed = Boolean(d?.closing);
    const editable = data && !closed && (data.isToday || perms.canBackdate);
    const allowedAction = (a) => (a.dir === 'IN' ? perms.canAddIn : perms.canAddOut);
    const quick = ACTIONS.filter(allowedAction);
    const more = MORE.filter(allowedAction);

    const saveEntry = async () => {
        const amount = Number(form.amount);
        if (!(amount > 0)) return setForm({ ...form, error: 'Enter an amount greater than zero.' });
        if (form.action.noteRequired && !form.note.trim()) return setForm({ ...form, error: 'Add a short note.' });
        if (form.action.dir === 'OUT' && d?.expected !== null && d?.expected !== undefined && d.expected - amount < -0.009 && !form.note.trim()) {
            return setForm({ ...form, error: 'The drawer goes below zero. Write who paid the extra from their pocket.' });
        }
        setSaving(true);
        try {
            await api('/entries', { method: 'POST', body: JSON.stringify({ date, category: form.action.category, amount, note: form.note, reference: form.reference }) });
            setForm(null); say('success', `${form.action.label}: ${inr(amount)} saved.`); load();
        } catch (e) { setForm({ ...form, error: e.message }); } finally { setSaving(false); }
    };

    const voidEntry = async (entry) => {
        const reason = window.prompt(`Cancel "${entry.label} ${inr(entry.amount)}"?\nWrite the reason:`);
        if (!reason || !reason.trim()) return;
        try { await api(`/entries/${entry._id}/void`, { method: 'PATCH', body: JSON.stringify({ reason }) }); say('success', 'Entry cancelled.'); load(); } catch (e) { say('error', e.message); }
    };

    const reopen = async () => {
        if (!window.confirm('Reopen this day? You can then add or cancel entries and count again.')) return;
        try { await api(`/close?date=${date}`, { method: 'DELETE' }); say('success', 'Day reopened.'); load(); } catch (e) { say('error', e.message); }
    };

    if (isHQ) return <div className={styles.page}><div className={styles.state}><AlertCircle size={22} /><strong>Select a branch</strong><span>The cash drawer is kept for each branch.</span></div></div>;

    return (
        <div className={styles.page}>
            <div className={styles.toolbar}>
                <div>
                    <h1>Cash drawer</h1>
                    <p>{rangeView ? `${longDate(range.startDate)} to ${longDate(range.endDate)}` : longDate(date)} · {activeStore?.storeName || activeStore?.name}</p>
                </div>
                <div className={styles.tools}>
                    <div className={styles.segment} role="tablist" aria-label="View">
                        <button type="button" role="tab" aria-selected={!rangeView} className={!rangeView ? styles.segOn : ''} onClick={() => setDate(date)}><CalendarDays size={13} /> Day</button>
                        <button type="button" role="tab" aria-selected={rangeView} className={rangeView ? styles.segOn : ''} onClick={() => setRange(range)}><CalendarRange size={13} /> Date range</button>
                    </div>
                    {rangeView ? (
                        <GlobalDateFilter startDate={range.startDate} endDate={range.endDate} onChange={(r) => r?.startDate && setRange(r)} defaultLabel="Date range" />
                    ) : (
                    <div className={styles.dateNav}>
                        <button type="button" onClick={() => setDate(shiftYmd(date, -1))} aria-label="Previous day"><ChevronLeft size={15} /></button>
                        <input type="date" value={date} max={todayYmd()} onChange={(e) => e.target.value && setDate(e.target.value)} />
                        <button type="button" onClick={() => setDate(shiftYmd(date, 1))} disabled={date >= todayYmd()} aria-label="Next day"><ChevronRight size={15} /></button>
                    </div>
                    )}
                    {canSeeReport && <button type="button" className={styles.ghost} onClick={() => navigate(`/reports/daily${date !== todayYmd() ? `?date=${date}` : ''}`)}><FileBarChart size={14} /> Full daily report</button>}
                </div>
            </div>

            {toast && <div className={`${styles.toast} ${toast.type === 'error' ? styles.toastErr : styles.toastOk}`}>{toast.type === 'error' ? <AlertCircle size={15} /> : <CheckCircle2 size={15} />} {toast.text}</div>}

            {rangeView ? (
                <RangeView api={api} range={range} storeId={storeId} onOpenDay={(d0) => setDate(d0)} />
            ) : loading && !data ? (
                <div className={styles.state}><Loader2 size={24} className={styles.spin} /><span>Counting the drawer…</span></div>
            ) : error ? (
                <div className={styles.state}><AlertCircle size={22} /><strong>Could not load</strong><span>{error}</span><button type="button" className={styles.ghost} onClick={load}>Try again</button></div>
            ) : d && (
                <>
                    {/* ---------- the number that matters ---------- */}
                    <section className={styles.hero}>
                        {blind ? (
                            <div className={styles.heroMain}>
                                <span>Blind count</span>
                                <strong className={styles.blindTitle}>Count the cash, then close</strong>
                                <small>The expected amount is only shown to the owner, so the count stays honest.</small>
                            </div>
                        ) : (
                            <div className={styles.heroMain}>
                                <span>{closed ? 'Expected at closing' : 'Should be in the drawer now'}</span>
                                <strong>{inr(d.expected)}</strong>
                                {d.expected < -0.009 && <em className={styles.belowZero}><AlertTriangle size={12} /> Below zero: someone paid {inr(Math.abs(d.expected))} from their pocket</em>}
                                <small>Opening {inr(d.opening)} · {d.openingSource}</small>
                            </div>
                        )}
                        <div className={styles.heroStats}>
                            {!blind && <div><span>Cash from bills</span><b className={styles.pos}>+ {inr(d.cashSales)}</b></div>}
                            {!blind && d.outsideCash > 0 && <div><span>Old udhaar / advance</span><b className={styles.pos}>+ {inr(d.outsideCash)}</b></div>}
                            <div><span>Cash in (entries)</span><b className={styles.pos}>+ {inr(d.cashIn)}</b></div>
                            <div><span>Cash out (entries)</span><b className={styles.neg}>− {inr(d.cashOut)}</b></div>
                            {!blind && d.expensesCash > 0 && <div><span>Expenses + vendors (cash)</span><b className={styles.neg}>− {inr(d.expensesCash)}</b></div>}
                        </div>
                        <div className={styles.heroSide}>
                            {closed ? (
                                <div className={`${styles.closedBox} ${blind || d.closing.difference === null ? styles.neutral : Math.abs(d.closing.difference) < 0.01 ? styles.ok : d.closing.difference < 0 ? styles.short : styles.extra}`}>
                                    <Lock size={15} />
                                    <span>Counted {inr(d.closing.countedCash)}</span>
                                    {!blind && d.closing.difference !== null && (
                                        <b>{Math.abs(d.closing.difference) < 0.01 ? 'Matches' : `${d.closing.difference < 0 ? 'Short' : 'Extra'} ${inr(Math.abs(d.closing.difference))}`}</b>
                                    )}
                                    {blind && <b>Day closed</b>}
                                    <small>by {d.closing.closedByName}</small>
                                    {perms.canReopen && <button type="button" onClick={reopen}><Unlock size={13} /> Reopen day</button>}
                                </div>
                            ) : perms.canClose ? (
                                <button type="button" className={styles.closeBtn} onClick={() => setCloseOpen(true)} disabled={!editable}><Lock size={15} /> Count and close drawer</button>
                            ) : null}
                        </div>
                    </section>

                    {/* ---------- quick actions ---------- */}
                    {editable && quick.length + more.length > 0 && (
                        <section className={styles.actions} style={{ gridTemplateColumns: `repeat(${Math.max(quick.length, 1)}, 1fr)${more.length ? ' auto' : ''}` }}>
                            {quick.map((a) => {
                                const Icon = a.icon;
                                return (
                                    <button key={a.category} type="button" className={`${styles.action} ${a.dir === 'IN' ? styles.actionIn : styles.actionOut}`}
                                        onClick={() => setForm({ action: a, amount: '', note: '', reference: '', error: '' })}>
                                        <span className={styles.actionIcon}><Icon size={17} /></span>
                                        <span><strong>{a.label}</strong><small>{a.hint}</small></span>
                                        <em>{a.dir === 'IN' ? '+' : '−'}</em>
                                    </button>
                                );
                            })}
                            {more.length > 0 && <div className={styles.moreWrap}>
                                <button type="button" className={styles.moreBtn} onClick={() => setShowMore((v) => !v)}><MoreHorizontal size={16} /> More</button>
                                {showMore && (
                                    <div className={styles.moreMenu}>
                                        {more.map((a) => { const Icon = a.icon; return (
                                            <button key={a.category} type="button" onClick={() => { setShowMore(false); setForm({ action: a, amount: '', note: '', reference: '', error: '' }); }}>
                                                <Icon size={14} /> <span><strong>{a.label}</strong><small>{a.hint}</small></span>
                                            </button>
                                        ); })}
                                    </div>
                                )}
                            </div>}
                        </section>
                    )}
                    {!editable && !closed && <p className={styles.note}>You can only add cash entries for today.</p>}

                    {/* ---------- entries ---------- */}
                    <section className={styles.card}>
                        <h2>Cash entries</h2>
                        {d.entries.length === 0 ? <p className={styles.empty}>No cash added or taken out on this day.</p> : (
                            <table className={styles.table}>
                                <thead><tr><th>Time</th><th>Type</th><th>Note</th><th>By</th><th style={{ textAlign: 'right' }}>Amount</th>{perms.canVoid && !closed && <th />}</tr></thead>
                                <tbody>
                                    {d.entries.map((e) => (
                                        <tr key={e._id} className={e.voided ? styles.voided : ''}>
                                            <td>{timeOf(e.createdAt)}</td>
                                            <td><span className={`${styles.tag} ${e.direction === 'IN' ? styles.tagIn : styles.tagOut}`}>{e.direction === 'IN' ? <ArrowDownLeft size={11} /> : <ArrowUpRight size={11} />} {e.label}</span>{e.wentNegative && <span className={styles.tagWarn}>Below zero</span>}</td>
                                            <td>{[e.note, e.reference && `Ref ${e.reference}`].filter(Boolean).join(' · ') || '—'}{e.voided && <small className={styles.voidNote}>Cancelled{e.voidedByName ? ` by ${e.voidedByName}` : ''}{e.voidReason ? `: ${e.voidReason}` : ''}</small>}</td>
                                            <td>{e.createdByName}</td>
                                            <td style={{ textAlign: 'right' }} className={e.direction === 'IN' ? styles.pos : styles.neg}><b>{e.direction === 'IN' ? '+' : '−'} {inr(e.amount)}</b></td>
                                            {perms.canVoid && !closed && <td>{!e.voided && <button type="button" className={styles.voidBtn} onClick={() => voidEntry(e)} title="Cancel entry"><Ban size={13} /></button>}</td>}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </section>

                    {/* ---------- payment modes today (context) ---------- */}
                    {data.payments && <section className={styles.card}>
                        <h2>Today's collection by mode</h2>
                        <div className={styles.modes}>
                            <div><span>Cash</span><b>{inr(data.payments.cash)}</b></div>
                            <div><span>UPI</span><b>{inr(data.payments.upi)}</b></div>
                            <div><span>Card</span><b>{inr(data.payments.card)}</b></div>
                            <div><span>Khata used</span><b>{inr(data.payments.wallet)}</b></div>
                            <div><span>Prepaid used</span><b>{inr(data.payments.prepaidCard)}</b></div>
                            <div><span>Udhaar given</span><b className={styles.neg}>{inr(data.payments.dueCreated)}</b></div>
                        </div>
                        <p className={styles.note}>Only cash affects the drawer. UPI and card go straight to the bank.</p>
                    </section>}
                </>
            )}

            {form && <EntryModal form={form} setForm={setForm} onSave={saveEntry} saving={saving} expected={d?.expected} maxCashOut={perms.maxCashOut} />}
            {closeOpen && d && <CloseModal date={date} expected={d.expected} blind={blind} api={api} onClose={() => setCloseOpen(false)} onDone={(msg) => { setCloseOpen(false); say('success', msg); load(); }} />}
        </div>
    );
}

function EntryModal({ form, setForm, onSave, saving, expected, maxCashOut }) {
    const a = form.action; const Icon = a.icon;
    const amount = Number(form.amount) || 0;
    const showAfter = expected !== null && expected !== undefined;
    const after = showAfter ? (a.dir === 'IN' ? expected + amount : expected - amount) : 0;
    const overLimit = a.dir === 'OUT' && maxCashOut > 0 && amount > maxCashOut;
    return (
        <div className={styles.overlay} role="presentation" onClick={() => !saving && setForm(null)}>
            <div className={styles.modal} role="dialog" aria-modal="true" aria-label={a.label} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHead}>
                    <span className={`${styles.actionIcon} ${a.dir === 'IN' ? styles.iconIn : styles.iconOut}`}><Icon size={17} /></span>
                    <div><h3>{a.label}</h3><p>{a.hint}</p></div>
                    <button type="button" className={styles.x} onClick={() => setForm(null)} aria-label="Close"><X size={16} /></button>
                </div>
                <label className={styles.field}>
                    <span>Amount</span>
                    <div className={styles.amountBox}><i>₹</i><input autoFocus type="number" min="0" inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value, error: '' })} placeholder="0" /></div>
                </label>
                {a.ref && <label className={styles.field}><span>{a.ref}</span><input value={form.reference} maxLength={80} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></label>}
                <label className={styles.field}>
                    <span>Note {a.noteRequired || (showAfter && a.dir === 'OUT' && after < -0.009) ? '' : '(optional)'}</span>
                    <input value={form.note} maxLength={300} placeholder={a.category === 'PETTY_EXPENSE' ? 'e.g. Tea and snacks' : 'Anything to remember'} onChange={(e) => setForm({ ...form, note: e.target.value, error: '' })} />
                </label>
                {showAfter && <div className={styles.preview}><span>Drawer after this</span><b className={after < 0 ? styles.neg : ''}>{inr(after)}</b></div>}
                {showAfter && a.dir === 'OUT' && after < -0.009 && <p className={styles.warnBox}><AlertTriangle size={13} /> The drawer goes below zero. That is allowed when someone pays from their pocket: write who in the note.</p>}
                {a.dir === 'OUT' && maxCashOut > 0 && <p className={overLimit ? styles.err : styles.hint}>You can take out up to {inr(maxCashOut)} in one entry.</p>}
                {form.error && <p className={styles.err}>{form.error}</p>}
                <div className={styles.modalFoot}>
                    <button type="button" className={styles.ghost} onClick={() => setForm(null)} disabled={saving}>Cancel</button>
                    <button type="button" className={a.dir === 'IN' ? styles.saveIn : styles.saveOut} onClick={onSave} disabled={saving}>{saving ? <Loader2 size={14} className={styles.spin} /> : <Plus size={14} />} Save {a.dir === 'IN' ? 'cash in' : 'cash out'}</button>
                </div>
            </div>
        </div>
    );
}

function CloseModal({ date, expected, blind, api, onClose, onDone }) {
    const [counts, setCounts] = useState({});
    const [coins, setCoins] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const counted = NOTES.reduce((s, n) => s + (Number(counts[n]) || 0) * Number(n), 0) + (Number(coins) || 0);
    const diff = blind ? 0 : Math.round((counted - (expected || 0)) * 100) / 100;

    const submit = async () => {
        setBusy(true); setErr('');
        try {
            const denominations = { ...Object.fromEntries(NOTES.map((n) => [n, Number(counts[n]) || 0])), coins: Number(coins) || 0 };
            const json = await api('/close', { method: 'POST', body: JSON.stringify({ date, denominations, countedCash: counted, note }) });
            onDone(json.message);
        } catch (e) { setErr(e.message); } finally { setBusy(false); }
    };

    return (
        <div className={styles.overlay} role="presentation" onClick={() => !busy && onClose()}>
            <div className={`${styles.modal} ${styles.modalWide}`} role="dialog" aria-modal="true" aria-label="Close drawer" onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHead}>
                    <span className={styles.actionIcon}><Lock size={17} /></span>
                    <div><h3>Count and close the drawer</h3><p>Count the notes in the drawer. Tomorrow starts from this amount.</p></div>
                    <button type="button" className={styles.x} onClick={onClose} aria-label="Close"><X size={16} /></button>
                </div>
                <div className={styles.notes}>
                    {NOTES.map((n) => (
                        <label key={n} className={styles.noteRow}>
                            <span>₹{n}</span><em>×</em>
                            <input type="number" min="0" inputMode="numeric" value={counts[n] || ''} placeholder="0" onChange={(e) => setCounts({ ...counts, [n]: e.target.value.replace(/\D/g, '') })} />
                            <b>{inr((Number(counts[n]) || 0) * Number(n))}</b>
                        </label>
                    ))}
                    <label className={styles.noteRow}>
                        <span>Coins</span><em>₹</em>
                        <input type="number" min="0" inputMode="numeric" value={coins} placeholder="0" onChange={(e) => setCoins(e.target.value.replace(/\D/g, ''))} />
                        <b>{inr(Number(coins) || 0)}</b>
                    </label>
                </div>
                {blind ? (
                    <div className={styles.countSummary} style={{ gridTemplateColumns: '1fr' }}>
                        <div><span>You counted</span><b>{inr(counted)}</b></div>
                    </div>
                ) : (
                    <div className={styles.countSummary}>
                        <div><span>Counted</span><b>{inr(counted)}</b></div>
                        <div><span>Should be</span><b>{inr(expected)}</b></div>
                        <div className={Math.abs(diff) < 0.01 ? styles.ok : diff < 0 ? styles.short : styles.extra}>
                            <span>{Math.abs(diff) < 0.01 ? 'Matches' : diff < 0 ? 'Short' : 'Extra'}</span><b>{Math.abs(diff) < 0.01 ? '✓' : inr(Math.abs(diff))}</b>
                        </div>
                    </div>
                )}
                {blind && (
                    <label className={styles.field}><span>Note for the owner (optional)</span><input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder="Anything the owner should know" /></label>
                )}
                {!blind && Math.abs(diff) >= 0.01 && (
                    <label className={styles.field}><span>Why is it different? (helps the owner)</span><input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Gave ₹50 change from own pocket" /></label>
                )}
                {err && <p className={styles.err}>{err}</p>}
                <div className={styles.modalFoot}>
                    <button type="button" className={styles.ghost} onClick={onClose} disabled={busy}>Cancel</button>
                    <button type="button" className={styles.saveIn} onClick={submit} disabled={busy}>{busy ? <Loader2 size={14} className={styles.spin} /> : <Lock size={14} />} Close drawer at {inr(counted)}</button>
                </div>
            </div>
        </div>
    );
}


/* ============================== date range view ============================== */
function RangeView({ api, range, storeId, onOpenDay }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    useEffect(() => {
        let alive = true;
        setLoading(true); setError('');
        api(`/range?from=${range.startDate}&to=${range.endDate}`)
            .then((j) => { if (alive) setData(j.data); })
            .catch((e) => { if (alive) setError(e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.startDate, range.endDate, storeId]);

    if (loading && !data) return <div className={styles.state}><Loader2 size={24} className={styles.spin} /><span>Adding up the days…</span></div>;
    if (error) return <div className={styles.state}><AlertCircle size={22} /><strong>Could not load</strong><span>{error}</span></div>;
    if (!data) return null;
    const t = data.totals || {};
    const blind = data.blind;
    const rows = [...data.rows].reverse();   // newest day first

    return (
        <>
            <section className={styles.rangeCards}>
                {!blind && <div className={styles.rangeDark}><span>Opening</span><b>{inr(t.opening)}</b><small>{longDate(data.from)}{data.openingSource ? ` · ${data.openingSource}` : ''}</small></div>}
                {!blind && <div><span>Cash from bills</span><b className={styles.pos}>+ {inr(t.cashSales)}</b>{t.outsideCash > 0 && <small>Old udhaar + {inr(t.outsideCash)}</small>}</div>}
                <div><span>Cash in (entries)</span><b className={styles.pos}>+ {inr(t.cashIn)}</b></div>
                <div><span>Cash out (entries)</span><b className={styles.neg}>− {inr(t.cashOut)}</b></div>
                {!blind && <div><span>Expenses + vendors (cash)</span><b className={styles.neg}>− {inr(t.expensesCash)}</b>{t.vendorCash > 0 && <small>Vendors {inr(t.vendorCash)}</small>}</div>}
                {!blind && <div className={t.closingCash < -0.009 ? styles.rangeRed : styles.rangeDark}><span>Cash at the end</span><b>{inr(t.closingCash)}</b><small>{longDate(data.to)}</small></div>}
                {!blind && <div className={Math.abs(t.shortExtra || 0) > 0.009 ? styles.rangeAmber : ''}><span>Short / extra (counts)</span><b>{Math.abs(t.shortExtra || 0) < 0.01 ? 'Matches' : `${t.shortExtra < 0 ? 'Short' : 'Extra'} ${inr(Math.abs(t.shortExtra))}`}</b><small>{t.daysClosed} of {data.days} days counted</small></div>}
                {!blind && t.daysNegative > 0 && <div className={styles.rangeRed}><span>Days below zero</span><b>{t.daysNegative}</b><small>Someone paid from their pocket</small></div>}
            </section>

            <section className={styles.card}>
                <h2>Day by day</h2>
                <div className={styles.tableScroll}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Day</th>
                                {!blind && <th style={{ textAlign: 'right' }}>Opening</th>}
                                {!blind && <th style={{ textAlign: 'right' }}>Bills (cash)</th>}
                                <th style={{ textAlign: 'right' }}>In</th>
                                <th style={{ textAlign: 'right' }}>Out</th>
                                {!blind && <th style={{ textAlign: 'right' }}>Expenses</th>}
                                {!blind && <th style={{ textAlign: 'right' }}>Should be</th>}
                                <th style={{ textAlign: 'right' }}>Counted</th>
                                {!blind && <th style={{ textAlign: 'right' }}>Difference</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.date} className={`${styles.rowLink} ${r.negative ? styles.negRow : ''}`} onClick={() => onOpenDay(r.date)} title="Open this day">
                                    <td><b>{longDate(r.date)}</b>{r.closedByName && <small className={styles.subtle}>Closed by {r.closedByName}</small>}</td>
                                    {!blind && <td style={{ textAlign: 'right' }} className={r.opening < 0 ? styles.neg : ''}>{inr(r.opening)}</td>}
                                    {!blind && <td style={{ textAlign: 'right' }}>{r.cashSales ? inr(r.cashSales + (r.outsideCash || 0)) : '—'}</td>}
                                    <td style={{ textAlign: 'right' }} className={styles.pos}>{r.cashIn ? `+ ${inr(r.cashIn)}` : '—'}</td>
                                    <td style={{ textAlign: 'right' }} className={styles.neg}>{r.cashOut ? `− ${inr(r.cashOut)}` : '—'}</td>
                                    {!blind && <td style={{ textAlign: 'right' }} className={styles.neg}>{r.expensesCash ? `− ${inr(r.expensesCash)}` : '—'}</td>}
                                    {!blind && <td style={{ textAlign: 'right' }}><b className={r.negative ? styles.neg : ''}>{inr(r.expected)}</b></td>}
                                    <td style={{ textAlign: 'right' }}>{r.counted === null || r.counted === undefined ? <span className={styles.subtle} style={{ display: 'inline' }}>Not counted</span> : inr(r.counted)}</td>
                                    {!blind && <td style={{ textAlign: 'right' }} className={r.difference < 0 ? styles.neg : r.difference > 0 ? styles.pos : ''}>{r.difference === null ? '—' : Math.abs(r.difference) < 0.01 ? 'Matches' : `${r.difference < 0 ? '−' : '+'} ${inr(Math.abs(r.difference))}`}</td>}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className={styles.card}>
                <h2>Cash entries in this period</h2>
                {data.entries.length === 0 ? <p className={styles.empty}>No cash added or taken out in this period.</p> : (
                    <div className={styles.tableScroll}>
                        <table className={styles.table}>
                            <thead><tr><th>Day</th><th>Type</th><th>Note</th><th>By</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
                            <tbody>
                                {[...data.entries].reverse().map((e) => (
                                    <tr key={e._id} className={e.voided ? styles.voided : ''}>
                                        <td>{longDate(e.businessDate)}<small className={styles.subtle}>{timeOf(e.createdAt)}</small></td>
                                        <td><span className={`${styles.tag} ${e.direction === 'IN' ? styles.tagIn : styles.tagOut}`}>{e.direction === 'IN' ? <ArrowDownLeft size={11} /> : <ArrowUpRight size={11} />} {e.label}</span>{e.wentNegative && <span className={styles.tagWarn}>Below zero</span>}</td>
                                        <td>{[e.note, e.reference && `Ref ${e.reference}`].filter(Boolean).join(' · ') || '—'}{e.voided && <small className={styles.voidNote}>Cancelled{e.voidReason ? `: ${e.voidReason}` : ''}</small>}</td>
                                        <td>{e.createdByName}</td>
                                        <td style={{ textAlign: 'right' }} className={e.direction === 'IN' ? styles.pos : styles.neg}><b>{e.direction === 'IN' ? '+' : '−'} {inr(e.amount)}</b></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </>
    );
}
